import {
  ACCEPT,
  GATED,
  GRAMMAR,
  PLAGIARISM,
  REJECT,
} from '@src/components/Cards/constants'
import Highlight from '@src/components/Editor/Highlight'
import { CardContent, ModuleContent } from '@src/components/Editor/types'
import { DEMO_PAPER } from '@src/constants/paper'
import {
  RECHECK_MENU_GRAMMAR,
  RECHECK_MENU_PLAGIARISM,
} from '@src/layouts/shared/Recheck'
import { CreatePaper } from '@src/queries/CreatePaper'
import {
  GetPaper,
  GetPaper_getPaper_feedbacks_feedbackContent,
} from '@src/queries/GetPaper'
import { GetPaperSubject } from '@src/queries/GetPaperSubject'
import { CREATE_PAPER, GET_PAPER, GET_SUBJECT, UPDATE_PAPER } from '@src/queries/Papers'
import { UpdatePaper } from '@src/queries/UpdatePaper'
import {
  acceptCard,
  CardActionsPayload,
  checkResults,
  checksComplete,
  readCard,
  rejectCard,
  runChecks,
  updateBulkMetadata,
} from '@src/state/checks/actions'
import {
  ActiveEntityKeyMeta,
  ActiveEntityKeyPayload,
  addComments,
  clearDocument,
  CommentsMap,
  copyText,
  createPaper,
  createPaperFailure,
  CreatePaperPayload,
  createPaperSuccess,
  deleteComment,
  deleteComments,
  deletePlagiarismScore,
  DocumentFile,
  DocumentInfo,
  documentReady,
  downloadPaper,
  ExternalLinkPayload,
  gateComments,
  generateEditorState,
  generateEditorStateFromPlainText,
  getDocument,
  getPaperSubject,
  GetPaperSubjectRequest,
  getPlainTextDocument,
  getPlainTextFail,
  handleDocumentCreate,
  HandleDocumentCreatePayload,
  loadModules,
  LoadModulesPayload,
  mergeChecksToState,
  MergeChecksToStatePayload,
  openExternalLink,
  PlainTextPayload,
  recheck,
  RecheckPayload,
  saveDocument,
  SaveDocumentPayload,
  seeSources,
  SeeSourcesPayload,
  setActiveEntityKey,
  setComments,
  setDocument,
  setEditorState,
  setFilename,
  setPlagiarismScore,
  showGatedComments,
  ShowGatedCommentsPayload,
  undo,
  updateComment,
  updateComments,
} from '@src/state/document/actions'
import {
  applyEntitiesToEditorState,
  deleteCommentFromEditor,
  findCommentRange,
  getCommentsFromRawEntity,
  getEntityComments,
  getIdToKeysMap,
  getNewEditorStateFromText,
  migrateComments,
  replaceText,
} from '@src/state/document/helpers/draftjs.helper'
import {
  getCategoryFromType,
  getEditorState,
  getEditorText,
  getFilteredComments,
  selectInteractableComments,
  selectInteractableGrammarComments,
  selectInteractablePlagComments,
  selectPaperSubject,
  selectPlagiarismSimilarityScore,
  selectUndoReplacementIndex,
} from '@src/state/document/selectors'
import { RootState } from '@src/state/types'
import {
  clearUndoReplacement,
  promptLoginWithRedirect,
  setGrammarCardCtaIndex,
  setNewUpload,
  showToast,
  showUndoReplacement,
} from '@src/state/workspace/actions'
import { DEFAULT_GRAMMAR_CARD_CTA_INDEX } from '@src/state/workspace/reducer'
import {
  getUploadId,
  selectCurrentCheckCategory,
  selectInitialCheckCategory,
} from '@src/state/workspace/selectors'
import { CheckMetadata, CheckWithMetadata } from '@src/types/checks'
import { Cookies, getCookie, removeCookie } from '@src/utils/cookies'
import { captureError } from '@src/utils/errors'
import graphApi from '@src/utils/http-clients/graphApi'
import { oneGraphClient } from '@src/utils/http-clients/oneGraph'
import { getWFELS, setWFELS } from '@src/utils/localStorage'
import {
  getExpertCheckWorkspacePath,
  getGrammarWorkspacePath,
  isExpertCheckPage,
  isWorkspacePage,
} from '@src/utils/routes'
import axios from 'axios'
import { push, replace } from 'connected-react-router'
import * as copy from 'copy-to-clipboard'
import {
  CompositeDecorator,
  convertFromRaw,
  convertToRaw,
  EditorState,
  RawDraftContentState,
} from 'draft-js'
import { saveAs } from 'file-saver'
import * as Cookie from 'js-cookie'
import { createLogic } from 'redux-logic'
import { getType } from 'typesafe-actions'
import { sendHiddenCards, sendNewCards } from '../analytics/actions'
import { appInitialized } from '../app/actions'
import { getLoggedIn, getUserID } from '../auth/selectors'
import { allChecksComplete } from '../checks/selectors'
import {
  getFeedbackCount,
  setFeedbackContent,
  setNetworkError,
} from '../expertcheck/actions'
import { ZOHO_COOKIE } from '../expertcheck/selectors'
import {
  isDemoActiveSelector,
  isOptimizelyClientReadySelector,
} from '../featureFlags/selectors'
import { PLAGIARISM_SOURCES_MODAL, showModal } from '../modals/actions'
import { getQueryParams, isDemoSelector } from '../router/selectors'
import {
  checkFromLocalStorage,
  loadFromLocalStorage,
  persistToLocalStorage,
  rehydrateWorkspace,
} from '../session/actions'
import { getStateToPersist } from '../session/selectors'
import { BLOCK_KEY, getSelectionState } from './document.helper'
import { getFlattenedRangesWithEntityData } from './helpers'
import { deleteNestedComments } from './helpers/comment.helper'
import { CheckCategory, EXPERT_CHECK, PaperContent } from './types'
import { GET_DOWNLOAD_URL } from '@src/oneGraphQueries/DownloadUrl'
import { DownloadUrl } from '@src/oneGraphQueries/__generated__/DownloadUrl'

export const demoSessionSetupLogic = createLogic<RootState>({
  type: [getType(appInitialized)],
  validate({ action, getState }, allow, reject) {
    const state = getState()
    const isDemo = isDemoSelector(state)
    const demoActive = isDemoActiveSelector(state)
    const clientReady = isOptimizelyClientReadySelector(state)
    if (demoActive && clientReady && isDemo) {
      allow(action)
    } else {
      reject(action)
    }
  },
  process({}, dispatch, done) {
    dispatch(
      generateEditorStateFromPlainText({
        text: DEMO_PAPER,
        id: (null as unknown) as string,
      })
    )
    done()
  },
})

export const hasInteraction = (cardState: string) =>
  cardState !== 'new' && cardState !== 'read' && cardState !== 'gated'

export const handleDocumentCreateLogic = createLogic<
  RootState,
  HandleDocumentCreatePayload
>({
  type: getType(handleDocumentCreate),
  process({ action, getState }, dispatch, done) {
    const {
      id,
      pushToEditor,
      uploadFromDemo,
    } = action.payload as HandleDocumentCreatePayload
    const state = getState()

    const isDemo = isDemoSelector(state)
    const isUsingDemoRouting = !uploadFromDemo && isDemo
    const initialCheckCategory = selectInitialCheckCategory(state)

    dispatch(setNewUpload({ isNew: true }))
    dispatch(getPlainTextDocument({ id }))

    if (initialCheckCategory === EXPERT_CHECK) {
      dispatch(push(getExpertCheckWorkspacePath(id, '', isUsingDemoRouting)))
    } else if (pushToEditor) {
      dispatch(push(getGrammarWorkspacePath(id, isUsingDemoRouting)))
    } else {
      dispatch(replace(getGrammarWorkspacePath(id, isUsingDemoRouting))) // This is to remove runChecks from the location
    }
    done()
  },
})

export const setFilenameLogic = createLogic<RootState, DocumentFile>({
  type: getType(setFilename),
  process({ getState, action }, dispatch, done) {
    const { name } = action.payload
    const state = getState()
    const wfeLs = getStateToPersist(state)
    setWFELS({
      ...wfeLs,
      filename: name,
    })
    done()
  },
})

export const setActiveEntityKeyLogic = createLogic<
  RootState,
  ActiveEntityKeyPayload,
  ActiveEntityKeyMeta
>({
  type: getType(setActiveEntityKey),
  transform({ getState, action }, next) {
    const { key, component } = action.payload as ActiveEntityKeyPayload
    const state = getState()
    const { editorState } = state.document
    const currentCategory = selectCurrentCheckCategory(state)
    const content = editorState.getCurrentContent()
    let data = action.payload as ActiveEntityKeyPayload
    if (key.length) {
      const entityComments = getEntityComments(content.getEntity(key))
      const entityComment = Object.entries(entityComments).find(
        ([_, c]) => c === currentCategory
      )
      if (entityComment) {
        const [id] = entityComment
        data = {
          key: id,
          component,
        }
      }
    }
    // pass to workspace reducer
    next({
      type: action.type,
      payload: data,
      meta: action.meta,
    })
  },
})

export const loadModulesLogic = createLogic<RootState, LoadModulesPayload>({
  type: getType(loadModules),
  process({ getState, action }, dispatch, done) {
    const state = getState()
    const { modules } = state
    const { editorState } = action.payload

    try {
      const decorators: any[] = []
      modules.forEach((module: ModuleContent) => {
        const type: CheckCategory = module.type === 'CITATIONS' ? PLAGIARISM : GRAMMAR
        decorators.push({
          component: Highlight,
          strategy: getEntityStrategy(type),
        })
      })

      const compositeDecorator = new CompositeDecorator(decorators)

      const newEditorState = EditorState.createWithContent(
        editorState.getCurrentContent(),
        compositeDecorator
      )
      dispatch(setEditorState({ editorState: newEditorState }))
    } catch (error) {
      console.error('FAILED TO LOAD MODULES', error)
    }
    done()
  },
})

function getEntityStrategy(category: CheckCategory) {
  return (
    contentBlock: Draft.ContentBlock,
    callback: () => void,
    contentState: Draft.ContentState
  ) => {
    contentBlock.findEntityRanges(character => {
      const entityKey = character.getEntity()
      if (entityKey === null) {
        return false
      }
      const categories = getCommentsFromRawEntity(contentState.getEntity(entityKey))
      return categories.some(c => c === category)
    }, callback)
  }
}

export const generateEditorStateFromPlainTextLogic = createLogic<
  RootState,
  PlainTextPayload
>({
  type: getType(generateEditorStateFromPlainText),
  process({ action }, dispatch, done) {
    const { text, id } = action.payload as PlainTextPayload
    const editorState = getNewEditorStateFromText(text)
    dispatch(setEditorState({ editorState }))
    dispatch(createPaper({ editorState }))
    dispatch(loadModules({ editorState }))
    dispatch(getDocument.success({ id }))
    dispatch(runChecks({ isRecheck: false, checkCategory: GRAMMAR }, {}))
    dispatch(getPaperSubject.request({ text }))
    done()
  },
})

export const createPaperLogic = createLogic<RootState, CreatePaperPayload>({
  type: getType(createPaper),
  validate({ getState, action }, allow, reject) {
    if (getLoggedIn(getState())) {
      return allow(action)
    }
    reject(action)
  },
  async process({ getState }, dispatch, done) {
    const state = getState()
    const { editorState, comments, plagiarismScore } = state.document
    const { id, filename } = state.workspace

    const content: PaperContent = {
      version: BLOCK_KEY,
      comments,
      rawContentState: convertToRaw(editorState.getCurrentContent()),
      plagiarismScore,
    }
    try {
      await graphApi.mutate<CreatePaper>({
        mutation: CREATE_PAPER,
        variables: {
          uuid: id,
          content,
          filename,
        },
      })
      dispatch(createPaperSuccess())
      sessionStorage.setItem('createPaperSuccess', 'true')
      dispatch(getDocument.request({ id }))
    } catch (error) {
      dispatch(createPaperFailure(error))
      captureError(error)
    }
    done()
  },
})

export const getPlainTextDocumentWtaiLogic = createLogic<RootState, DocumentInfo>({
  type: getType(getPlainTextDocument),
  async process({ action, getState }, dispatch, done) {
    const { id } = action.payload as DocumentInfo
    const state = getState()
    const uploadId = getUploadId(state)

    const getUrlFromQuery = async () => {
      const {
        data: { downloadUrl: url },
      } = await oneGraphClient.query<DownloadUrl>({
        query: GET_DOWNLOAD_URL,
        fetchPolicy: 'network-only',
        variables: { id: uploadId },
      })
      return url
    }

    try {
      const url = await getUrlFromQuery()
      const text = await axios.get(url)

      dispatch(generateEditorStateFromPlainText({ text: text.data, id }))
      dispatch(setDocument({ id }))
    } catch (error) {
      dispatch(getPlainTextFail(error))
    }
    done()
  },
})

export const handleGetDocumentFailLogic = createLogic<RootState>({
  type: getType(getDocument.failure),
  process({ getState }, dispatch, done) {
    dispatch(push('/document-not-found'))
    done()
  },
})

export const wtaiGetDocumentLogic = createLogic<RootState, DocumentInfo>({
  type: getType(getDocument.request),
  validate({ getState, action }, allow, reject) {
    const state = getState()
    const loggedIn = getLoggedIn(state)
    const { id } = action.payload
    const search = getQueryParams(state)
    const authSuccess = search?.authSuccess
    const hasLocalStorageContent = !!getWFELS()
    const createPaperSuccess = sessionStorage.getItem('createPaperSuccess')
    if (
      state.workspace.newUpload ||
      (authSuccess === 'true' && createPaperSuccess !== 'true')
    ) {
      return reject(action)
    } else if (!loggedIn && hasLocalStorageContent) {
      return reject(checkFromLocalStorage({ id }))
    } else if (!loggedIn) {
      return reject(promptLoginWithRedirect())
    }
    allow(action)
  },
  async process({ getState, action }, dispatch, done) {
    const { id } = action.payload
    const state = getState()

    try {
      dispatch(getFeedbackCount())
      const { data } = await graphApi.query<GetPaper>({
        query: GET_PAPER,
        variables: {
          uuid: id,
        },
        errorPolicy: 'all',
        fetchPolicy: 'network-only',
      })

      const { content, feedbacks, filename } = data.getPaper
      const feedbacksWithContent = feedbacks.filter(
        feedback => feedback.feedbackContent !== null
      )
      if (feedbacksWithContent?.length) {
        const [first] = feedbacksWithContent.sort(
          (a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()
        )
        const {
          __typename,
          ...feedbackContent
        } = first?.feedbackContent as GetPaper_getPaper_feedbacks_feedbackContent
        const { feedbackInApp, feedbackAvailable, feedbackIsValid } = feedbackContent
        const feedbackIsAvailable = feedbackInApp && feedbackAvailable && feedbackIsValid
        dispatch(setFeedbackContent(feedbackContent))

        // If the ticket is closed, remove its cookie
        if (feedbackIsAvailable) {
          const zohoCookieName = `${ZOHO_COOKIE}-${getUserID(state)}`
          Cookie.remove(zohoCookieName)
        }
        // If an upload-pending cookie is present for the retrieved paper, remove it
        const uploadPendingUploadUuid = getCookie(Cookies.EcUploadPending)
        if (uploadPendingUploadUuid === id) {
          removeCookie(Cookies.EcUploadPending, state.site)
        }
      }

      // should only increment for non-backwards compatible change
      if (content?.version === BLOCK_KEY) {
        const {
          rawContentState: contentState,
          comments,
          plagiarismScore,
        } = content as PaperContent
        dispatch(addComments(comments))
        dispatch(generateEditorState(contentState))
        if (typeof plagiarismScore === 'number') {
          dispatch(setPlagiarismScore({ score: plagiarismScore }))
        }
      } else {
        // assume content is previous schema where content is rawContentState
        const contentState = convertFromRaw(content)
        const newEditorState = getNewEditorStateFromText(contentState.getPlainText())
        dispatch(loadModules({ editorState: newEditorState }))
        dispatch(runChecks({ isRecheck: false, checkCategory: GRAMMAR }, {}))
      }
      dispatch(getDocument.success({ id }))
      dispatch(setDocument({ id }))
      dispatch(setFilename({ name: filename || '' }))
    } catch (e) {
      dispatch(setNetworkError({ networkError: true }))
      dispatch(getDocument.failure(e))
    }
    dispatch(setGrammarCardCtaIndex({ ctaIndex: DEFAULT_GRAMMAR_CARD_CTA_INDEX }))
    done()
  },
})

export const wtaiSaveDocumentLogic = createLogic<RootState, SaveDocumentPayload>({
  type: getType(saveDocument.request),
  debounce: 500,
  cancelType: getType(clearDocument),
  validate({ getState, action }, allow, reject) {
    const state = getState()
    const isDemo = isDemoSelector(state)

    if (!isWorkspacePage(state.router?.location.pathname) && !isDemo) {
      return reject(action)
    }
    const isLoggedIn = getLoggedIn(state)
    if (!isLoggedIn || isDemo) {
      return reject(persistToLocalStorage())
    }
    const createPaperSuccess = sessionStorage.getItem('createPaperSuccess')
    if (state.document.saving || createPaperSuccess !== 'true') {
      return reject(action)
    }

    allow(action)
  },
  async process({ getState, action }, dispatch, done) {
    const state = getState()
    const { id } = state.workspace
    const payload: SaveDocumentPayload = action.payload
    const editorState: EditorState = payload.editorState || getEditorState(state)
    const contentState = convertToRaw(editorState.getCurrentContent())
    const { version, comments, plagiarismScore } = payload.document || state.document

    const updatePaperPayload: PaperContent = {
      version,
      rawContentState: contentState,
      comments,
      plagiarismScore,
    }

    try {
      await graphApi.mutate<UpdatePaper>({
        mutation: UPDATE_PAPER,
        variables: {
          uuid: id,
          content: updatePaperPayload,
        },
      })
      dispatch(saveDocument.success({}))

      if (payload.manual) {
        dispatch(showToast({ toast: 'saved' }))
      }
    } catch (error) {
      captureError(error)
      dispatch(saveDocument.failure(error))
    }
    done()
  },
})

export const rejectCardLogic = createLogic<RootState, CardActionsPayload>({
  type: getType(rejectCard),
  process({ action, getState }, dispatch, done) {
    const { card } = action.payload as CardActionsPayload
    const state = getState()
    const editorState = getEditorState(state)
    const { id } = state.workspace

    // handle comment in state
    dispatch(deleteComment({ id: card.id }))

    // Move the grammar card cta up one if there is an undo card before it
    const undoIndex = selectUndoReplacementIndex(state)?.undoListIndex
    if (undoIndex !== undefined) {
      const { grammarCardCtaIndex } = state.workspace
      const hasUndoCardBeforeGrammarCta = undoIndex < grammarCardCtaIndex
      if (hasUndoCardBeforeGrammarCta) {
        dispatch(setGrammarCardCtaIndex({ ctaIndex: grammarCardCtaIndex - 1 }))
      }
    }

    // handle comment in DraftJS
    const cS = editorState.getCurrentContent()
    // clone contentState, possibly a better way to do this
    const oldCS = convertFromRaw(convertToRaw(cS))
    const newCS = deleteCommentFromEditor({
      id: card.id,
      entityKeys: card.entityKeys,
      contentState: cS,
    })

    const newES = EditorState.push(editorState, newCS, 'apply-entity')
    dispatch(saveDocument.request({ editorState: newES, id }))
    dispatch(setEditorState({ editorState: newES }))

    const hasPlagComments = selectInteractablePlagComments(state).some(
      c => c.type === PLAGIARISM && c.id !== card.id
    )
    if (!hasPlagComments) {
      dispatch(deletePlagiarismScore())
    }

    const { start } = card
    dispatch(
      showUndoReplacement({
        undo: {
          comment: card,
          undoEditorStartIndex: start,
          undoType: REJECT,
          lastContentState: oldCS,
          plagiarismScore: selectPlagiarismSimilarityScore(state),
        },
      })
    )
    done()
  },
})

export const readCardLogic = createLogic<RootState, CardActionsPayload>({
  type: getType(readCard),
  cancelType: [getType(acceptCard), getType(readCard)],
  validate({ action, getState }, allow, reject) {
    const { card } = action.payload
    const commentsMap = getState().document.comments
    if (commentsMap[card.id]) {
      return allow(action)
    }
    return reject(action)
  },
  process({ action, getState }, dispatch, done) {
    const { document } = getState()
    const { card } = action.payload
    const updatedComment = { ...card, cardState: 'read' as const }
    dispatch(updateComment(updatedComment))
    dispatch(
      saveDocument.request({
        document: {
          ...document,
          comments: {
            ...document.comments,
            [card.id]: updatedComment,
          },
        },
      })
    )
    done()
  },
})

export const acceptCardLogic = createLogic<RootState, CardActionsPayload>({
  type: getType(acceptCard),
  process({ action, getState }, dispatch, done) {
    const { card, selectedReplacement, type } = action.payload as CardActionsPayload
    const state = getState()
    const { id } = state.workspace
    const editorState = getEditorState(state)
    const contentState = editorState.getCurrentContent()
    // clone contentState, possibly a better way to do this
    const oldCS = convertFromRaw(convertToRaw(contentState))

    const { entityKeys, id: commentId } = card

    // handle comment in state
    dispatch(deleteComment({ id: card.id }))

    // Move the grammar card cta up one if there is an undo card before it
    const undoIndex = selectUndoReplacementIndex(state)?.undoListIndex
    if (undoIndex !== undefined) {
      const { grammarCardCtaIndex } = state.workspace
      const hasUndoCardBeforeGrammarCta = undoIndex < grammarCardCtaIndex
      if (hasUndoCardBeforeGrammarCta) {
        dispatch(setGrammarCardCtaIndex({ ctaIndex: grammarCardCtaIndex - 1 }))
      }
    }

    // handle comment data in DraftJS
    const newCS = deleteCommentFromEditor({
      id: commentId,
      entityKeys,
      contentState,
    })

    // replace text with selected text
    let newContentState
    const range = findCommentRange(contentState, entityKeys)
    if (type !== PLAGIARISM && selectedReplacement && range) {
      newContentState = replaceText({ contentState: newCS, range, selectedReplacement })
      dispatch(
        showUndoReplacement({
          undo: {
            comment: card,
            undoEditorStartIndex: range.offset,
            undoType: ACCEPT,
            lastContentState: oldCS,
          },
        })
      )

      /* 
      text replacement will clear all entities nested in that range, 
      we also need to clear the associated comments in state
      */
      // ordered comments will still have original comment in state
      const orderedComments = selectInteractableGrammarComments(state).sort(
        (a, b) => a.start - b.start
      )
      const idsToDelete = deleteNestedComments({ orderedComments, entityKeys, commentId })
      dispatch(deleteComments({ ids: idsToDelete }))
    }
    const newEditorState = EditorState.push(
      editorState,
      newContentState || newCS,
      'apply-entity'
    )
    dispatch(saveDocument.request({ editorState: newEditorState, id }))
    dispatch(setEditorState({ editorState: newEditorState }))
    done()
  },
})

export const mergeChecksToStateLogic = createLogic<RootState, MergeChecksToStatePayload>({
  type: getType(mergeChecksToState),
  async process({ getState, action }, dispatch, done) {
    const state = getState()
    const { editorState } = state.document

    const documentId = state.workspace.id

    const { comments } = action.payload
    const rangesWithEntityComments = getFlattenedRangesWithEntityData(comments)

    const newEditorState = applyEntitiesToEditorState(
      rangesWithEntityComments,
      editorState
    )

    const idToKeys = getIdToKeysMap(
      newEditorState.getCurrentContent().getFirstBlock(),
      rangesWithEntityComments
    )
    const commentsMap = comments.reduce<CommentsMap>((acc, c) => {
      const comment = { ...c, entityKeys: idToKeys[c.id] }
      acc[c.id] = comment
      return acc
    }, {})

    dispatch(setComments(commentsMap))
    dispatch(setEditorState({ editorState: newEditorState }))
    dispatch(loadModules({ editorState: newEditorState }))
    dispatch(saveDocument.request({ editorState: newEditorState, id: documentId }))
    dispatch(gateComments())
    dispatch(setGrammarCardCtaIndex({ ctaIndex: DEFAULT_GRAMMAR_CARD_CTA_INDEX }))
    done()
  },
})

export const makeEditorStateLogic = createLogic<RootState, RawDraftContentState>({
  type: getType(generateEditorState),
  async process({ getState, action }, dispatch, done) {
    const state = getState()

    const content = action.payload
    const editorState = EditorState.createWithContent(convertFromRaw(content))
    const blockKey = editorState
      .getCurrentContent()
      .getFirstBlock()
      .getKey()
    // no longer have to check, WTP-1665
    if (blockKey !== BLOCK_KEY) {
      const newEditorState = getNewEditorStateFromText(
        editorState.getCurrentContent().getPlainText()
      )
      dispatch(loadModules({ editorState: newEditorState }))
      dispatch(runChecks({ isRecheck: false, checkCategory: GRAMMAR }, {}))
    } else {
      // TODO: WTP-1695 to unify loading from persistence layer
      const newComments = migrateComments(
        editorState.getCurrentContent(),
        state.document.comments
      )
      dispatch(showGatedComments({ editorState }))
      dispatch(setComments(newComments))
      dispatch(loadModules({ editorState }))
    }

    if (!selectPaperSubject(state)) {
      dispatch(
        getPaperSubject.request({ text: editorState.getCurrentContent().getPlainText() })
      )
    }
    done()
  },
})

export const filterOverlapsLogic = createLogic<RootState, CheckWithMetadata>({
  type: getType(checkResults.success),
  async process({ getState, action }, dispatch, done) {
    const state = getState()
    const check = action.payload as CheckWithMetadata

    const { metadata, summary } = check

    const enhancedMetadata: CheckMetadata[] = metadata.map(comment => ({
      ...comment,
      overallMatchPercentage: summary && summary.overallMatchPercentage,
      submissionId: summary && summary.submissionId,
    }))

    const { shown, hidden } = getFilteredComments(enhancedMetadata)(state)

    const hiddenCards: string[] = hidden.map(card => card.id)
    dispatch(
      updateBulkMetadata.request({
        ids: hiddenCards,
        state: 'hidden',
      })
    )
    dispatch(sendHiddenCards(hidden))
    dispatch(mergeChecksToState({ comments: shown }))
    done()
  },
})

export const gateCommentsLogic = createLogic<RootState>({
  type: getType(gateComments),
  validate({ getState, action }, allow, reject) {
    const state = getState()
    const complete = allChecksComplete(state.checks, state.workspace.checksToExpect)
    if (complete && !state.workspace.partialPlagiarismRunning) {
      return allow(action)
    }
    reject(action)
  },
  process({ getState }, dispatch, done) {
    const state = getState()

    const expectedCheckCategory = selectCurrentCheckCategory(state)
    const comments = selectInteractableComments(state)

    const { toGate, toShow } = {
      toShow: comments,
      toGate: [],
    }

    /**
     * we can only run one check category at a time, this helps us filter
     * out the comments from the other category we're not currently checking
     * to ensure we're only sending the new cards to analytics
     */
    const outCommentsWithUnexpectedCategory = (comment: CardContent) =>
      expectedCheckCategory === getCategoryFromType(comment.type)
    const newToGate = toGate.filter(outCommentsWithUnexpectedCategory)
    const newToShow = toShow.filter(outCommentsWithUnexpectedCategory)

    dispatch(checksComplete())
    dispatch(sendNewCards({ newCards: newToShow, gatedCards: newToGate }))
    dispatch(documentReady())
    done()
  },
})

export const documentReadyLogic = createLogic<RootState>({
  type: getType(documentReady),
  process({ getState }, dispatch, done) {
    dispatch(saveDocument.request({ editorState: getState().document.editorState }))
    done()
  },
})

export const showGatedCommentsLogic = createLogic<RootState, ShowGatedCommentsPayload>({
  type: getType(showGatedComments),
  process({ getState }, dispatch, done) {
    const state = getState()
    const gatedComments = selectInteractableComments(state).filter(
      comment => comment.cardState === GATED
    )

    const ungatedComments = gatedComments.map(c => ({ ...c, cardState: 'new' as const }))
    dispatch(updateComments(ungatedComments))

    const updatedComments = ungatedComments.reduce<CommentsMap>((map, c) => {
      return { ...map, [c.id]: c }
    }, {})
    dispatch(
      saveDocument.request({
        document: {
          ...state.document,
          comments: { ...state.document.comments, ...updatedComments },
        },
      })
    )
    done()
  },
})

export const openExternalLinkLogic = createLogic<RootState, ExternalLinkPayload>({
  type: getType(openExternalLink),
  process({ action }, dispatch, done) {
    const { source } = action.payload as ExternalLinkPayload
    window.open(source, '_blank')
    done()
  },
})

export const copyToClipboardLogic = createLogic<RootState>({
  type: getType(copyText),
  process({ getState }, dispatch, done) {
    const editorState = getEditorState(getState())
    const currentContent = editorState.getCurrentContent()
    copy(currentContent.getPlainText())

    const selection = getSelectionState(
      0,
      // Get last char in editor
      currentContent.getFirstBlock().getLength()
    )
    dispatch(
      setEditorState({
        editorState: EditorState.forceSelection(editorState, selection),
      })
    )
    dispatch(showToast({ toast: 'copied' }))
    done()
  },
})

export const downloadPaperLogic = createLogic<RootState>({
  type: getType(downloadPaper),
  process({ getState }, dispatch, done) {
    const text = getEditorText(getState())
    const blob = new Blob([text], { type: 'text/plain' })
    try {
      saveAs(blob, 'paper.txt')
    } catch (error) {
      captureError(error)
    }
    done()
  },
})

export const seeSourcesLogic = createLogic<RootState, SeeSourcesPayload>({
  type: getType(seeSources),
  process({ action }, dispatch, done) {
    const { metadataId } = action.payload as SeeSourcesPayload
    dispatch(
      showModal({
        name: PLAGIARISM_SOURCES_MODAL,
        modalProps: {
          metadataId,
        },
      })
    )
    done()
  },
})

export const rehydrateWorkspaceLogic = createLogic<RootState>({
  type: getType(rehydrateWorkspace),
  validate({ getState, action }, allow, reject) {
    const loggedIn = getLoggedIn(getState())
    if (loggedIn) {
      return reject(action)
    }
    allow(action)
  },
  process(_, dispatch, done) {
    const wfeLs = getWFELS()
    if (wfeLs) {
      dispatch(loadFromLocalStorage(wfeLs))
    }
    done()
  },
})

export const recheckLogic = createLogic<RootState, RecheckPayload>({
  type: getType(recheck),
  process({ getState, action }, dispatch, done) {
    const state = getState()
    const isDemo = isDemoSelector(state)
    const { category } = action.payload

    if (isExpertCheckPage(state.router?.location.pathname)) {
      dispatch(push(getGrammarWorkspacePath(state.workspace.id, isDemo)))
    }

    let origin = ''

    switch (category) {
      case PLAGIARISM:
        origin = RECHECK_MENU_PLAGIARISM
        break
      case GRAMMAR:
        origin = RECHECK_MENU_GRAMMAR
        break
      default:
    }

    dispatch(runChecks({ isRecheck: true, checkCategory: category }, { origin }))
    done()
  },
})

export const handleUndoLogic = createLogic<RootState>({
  type: getType(undo),
  process({ getState }, dispatch, done) {
    const state = getState()
    const { undoReplacement, id } = state.workspace
    const { editorState, comments } = state.document
    if (undoReplacement) {
      const { lastContentState: lastCS, comment, plagiarismScore } = undoReplacement

      // reset comment into state
      const undoComments = { ...comments, [comment.id]: comment }
      const migratedComments = migrateComments(lastCS, undoComments)
      dispatch(setComments(migratedComments))

      // reset DraftJS
      const lastES = EditorState.push(editorState, lastCS, 'apply-entity')
      dispatch(clearUndoReplacement())
      dispatch(saveDocument.request({ editorState: lastES, id }))
      dispatch(
        setEditorState({
          editorState: lastES,
        })
      )

      // reset plag score
      if (
        plagiarismScore &&
        !selectPlagiarismSimilarityScore(state) &&
        comment.type === PLAGIARISM
      ) {
        dispatch(setPlagiarismScore({ score: plagiarismScore }))
      }
    }
    done()
  },
})

export const paperSubjectLogic = createLogic<RootState, GetPaperSubjectRequest>({
  type: getType(getPaperSubject.request),
  validate({ action }, allow, reject) {
    if (!action.payload.text.trim()) {
      return reject(action)
    }
    return allow(action)
  },
  async process({ action }, dispatch, done) {
    const { text } = action.payload

    try {
      const { data } = await graphApi.query<GetPaperSubject>({
        query: GET_SUBJECT,
        fetchPolicy: 'network-only',
        variables: {
          paper: text,
        },
      })
      const { subject, score, kgName, kgUuid, metadata } = data.getPaperSubjectPrediction
      dispatch(getPaperSubject.success({ subject, score, kgName, kgUuid, metadata }))
    } catch {
      dispatch(getPaperSubject.failure())
    }
    done()
  },
})
export const documentLogic = [
  demoSessionSetupLogic,
  recheckLogic,
  handleDocumentCreateLogic,
  handleGetDocumentFailLogic,
  generateEditorStateFromPlainTextLogic,
  makeEditorStateLogic,
  loadModulesLogic,
  setActiveEntityKeyLogic,
  readCardLogic,
  acceptCardLogic,
  rejectCardLogic,
  openExternalLinkLogic,
  copyToClipboardLogic,
  downloadPaperLogic,
  seeSourcesLogic,
  gateCommentsLogic,
  filterOverlapsLogic,
  mergeChecksToStateLogic,
  showGatedCommentsLogic,
  createPaperLogic,
  wtaiSaveDocumentLogic,
  setFilenameLogic,
  wtaiGetDocumentLogic,
  rehydrateWorkspaceLogic,
  documentReadyLogic,
  getPlainTextDocumentWtaiLogic,
  handleUndoLogic,
  paperSubjectLogic,
]



// WEBPACK FOOTER //
// ./src/state/document/logic.ts